import type { Dispatch, SetStateAction } from 'react';

import { useCallback, useEffect } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import { useAppSelector } from '@xver/hooks/useAppSelector';

import { Endpoints } from '@xver/apis';
import { requestAuthUser } from '@xver/store/auth/auth.actions';
import { setErrorMessage } from '@xver/store/error/error.actions';
import { useNavigate } from 'react-router';
import storage from '../../../../../../utils/storage';
import { PAGE_TYPE, PageType } from '../../../constants';
import { useResendCountdown } from './useResendCountdown';

const RESEND_TIMEOUT_MS = 10 * 60 * 1000;

interface usePhoneNumberVerificationProps {
    setPageType: Dispatch<SetStateAction<PageType>>;
}

export function usePhoneNumberVerification({ setPageType }: usePhoneNumberVerificationProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['notifications', 'validation']);

    const { phoneNumber, verificationToken } = useAppSelector((state) => state.authPage);

    const {
        formState: { isSubmitting },
        handleSubmit,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            phoneNumber: phoneNumber as string,
            otpRef: '',
            otpCode: '',
        },
    });

    const onSubmit = handleSubmit(async (values) => {
        if (!verificationToken) {
            dispatch(setErrorMessage(t('notifications:auth.missingVerificationToken')));
            return;
        }

        if (values.otpCode.length !== 6) {
            dispatch(setErrorMessage(t('validation:otp.mustBe6Digits')));
            return;
        }

        try {
            const response = await Endpoints.v1.Authentication.verifyPhone(verificationToken, {
                otpCode: values.otpCode,
                otpRef: values.otpRef,
            });

            if (response?.token) {
                storage.set('_zugang', response.token);
                await dispatch(requestAuthUser());
                navigate('/dashboard', { replace: true });
            }
        } catch (error: any) {
            dispatch(setErrorMessage(t('notifications:auth.verificationFailedMessage')));
        }
    });

    const {
        isAvailable: isResendAvailable,
        formattedRemainingTime: resendCountdown,
        restart,
    } = useResendCountdown({ initialDurationMs: RESEND_TIMEOUT_MS });

    const handleResend = useCallback(() => {
        if (!isResendAvailable) {
            return;
        }

        restart();
    }, [isResendAvailable, restart]);

    useEffect(() => {
        if (!verificationToken || !phoneNumber) {
            setPageType(PAGE_TYPE.SIGN_IN);
            return;
        }

        const sendCode = async () => {
            try {
                const response =
                    await Endpoints.v1.Authentication.sendPhoneVerification(verificationToken);
                if (response.data) {
                    setValue('otpRef', response.data.otpRef);
                }
            } catch (error: any) {
                dispatch(setErrorMessage(t('notifications:auth.sendCodeFailed')));
            }
        };

        sendCode();
    }, [verificationToken, phoneNumber, setPageType, dispatch, setValue, t]);
    return {
        isSubmitting,
        onSubmit,
        setValue,
        watch,
        phoneNumber,
        isResendAvailable,
        resendCountdown,
        handleResend,
    };
}
